import { useCallback } from "react";
import { listWorkflows, type WorkflowPurpose, type WorkflowSummary } from "../../../api/content/workflowsApi";
import { useLoad, type LoadHandle } from "../../../shared/loading/useLoad";

export interface ShippedWorkflow {
  /** `null` if the project runs no Workflow for this job: it pins no library that ships one. */
  readonly workflow: WorkflowSummary | null;
  /** The model version listed, which the new Thread binds. */
  readonly contentVersion: string;
}

/** The Workflow for one shipped job among `workflows`: one the project runs first; `null` for none. */
export function shippedWorkflowOf(workflows: readonly WorkflowSummary[], purpose: WorkflowPurpose): WorkflowSummary | null {
  const matching = workflows.filter((candidate) => candidate.purpose === purpose);
  return matching.find((candidate) => candidate.runsInProject) ?? matching[0] ?? null;
}

/**
 * The Workflow that does one shipped job in this project ("Edit an item", "Keep a memory",
 * ADR-0031): found by the purpose the library marks it with (D3), never by name or ID. A
 * Workflow the project runs comes first.
 */
export async function readShippedWorkflow(projectId: string, purpose: WorkflowPurpose, signal?: AbortSignal): Promise<ShippedWorkflow> {
  const listing = await listWorkflows(projectId, null, signal);
  return { workflow: shippedWorkflowOf(listing.workflows, purpose), contentVersion: listing.version };
}

/** `readShippedWorkflow`, loaded with the view. */
export function useShippedWorkflow(projectId: string, purpose: WorkflowPurpose): LoadHandle<ShippedWorkflow> {
  const load = useCallback((signal: AbortSignal) => readShippedWorkflow(projectId, purpose, signal), [projectId, purpose]);
  return useLoad(load);
}
